import { createContext, useContext, useMemo, useRef } from 'react';
import type { ReactNode } from 'react';
import { addFiles, setPicture } from '../state/edit.js';
import { useAppState, useStore } from './context.js';

export interface Pickers {
  /** Open the device's file picker for photos and videos to add to the grid. */
  readonly media: () => void;
  /** Open it for one image that becomes your picture. */
  readonly picture: () => void;
  /** Open the device's camera for a photo to add to the grid (owner, 4 October 2026). */
  readonly camera: () => void;
  /** Open it to record a video to add to the grid. Does nothing where videos are not taken. */
  readonly record: () => void;
}
const PickersContext = createContext<Pickers>({ media: () => undefined, picture: () => undefined, camera: () => undefined, record: () => undefined });
export const usePickers = (): Pickers => useContext(PickersContext);

/** The hidden file inputs of the editor (template "surface") and the functions that open them. */
export function PickersProvider({ children }: { readonly children: ReactNode }) {
  const s = useStore(), video = useAppState().caps.video;
  const media = useRef<HTMLInputElement | null>(null), picture = useRef<HTMLInputElement | null>(null);
  const camera = useRef<HTMLInputElement | null>(null), record = useRef<HTMLInputElement | null>(null);
  const pickers = useMemo<Pickers>(() => ({
    media: () => media.current?.click(), picture: () => picture.current?.click(), camera: () => camera.current?.click(), record: () => record.current?.click(),
  }), []);
  return <PickersContext.Provider value={pickers}>
    <input ref={media} type="file" multiple accept={video ? 'image/*,video/*' : 'image/*'} data-v1-pick="media" tabIndex={-1} aria-hidden="true" style={{ display: 'none' }}
      onChange={(event) => { addFiles(s, Array.from(event.target.files ?? []), null); event.target.value = ''; }} />
    {/*
      `capture` opens the back camera on a phone. One input takes one kind of capture: asked for photos and
      videos together, Android opens its gallery instead of the camera (owner, 4 October 2026).
    */}
    <input ref={camera} type="file" accept="image/*" capture="environment" data-v1-pick="camera" tabIndex={-1} aria-hidden="true" style={{ display: 'none' }}
      onChange={(event) => { addFiles(s, Array.from(event.target.files ?? []), null); event.target.value = ''; }} />
    {video && <input ref={record} type="file" accept="video/*" capture="environment" data-v1-pick="record" tabIndex={-1} aria-hidden="true" style={{ display: 'none' }}
      onChange={(event) => { addFiles(s, Array.from(event.target.files ?? []), null); event.target.value = ''; }} />}
    <input ref={picture} type="file" accept="image/jpeg,image/png,image/webp" data-v1-pick="picture" tabIndex={-1} aria-hidden="true" style={{ display: 'none' }}
      onChange={(event) => { const file = event.target.files?.[0]; if (file) setPicture(s, file); event.target.value = ''; }} />
    {children}
  </PickersContext.Provider>;
}
